import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const DEFAULT_PREFS: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const STORAGE_KEY = "cookie-consent";

export function getCookiePreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFS);

  useEffect(() => {
    const existing = getCookiePreferences();
    if (!existing) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (preferences: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    setVisible(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const saveSelection = () => save(prefs);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:p-6 pointer-events-none">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/40 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}}
      />

      {/* Banner */}
      <div className="relative pointer-events-auto w-full max-w-2xl rounded-2xl border border-border bg-card/95 backdrop-blur-xl p-6 shadow-2xl animate-fade-up">
        <h3 className="text-lg font-bold mb-2 text-foreground" style={{ fontFamily: "Poppins, sans-serif", fontStyle: "normal", textTransform: "none" }}>
          🍪 Cookie-Einstellungen
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Wir verwenden Cookies, um dir die beste Erfahrung auf unserer Website zu bieten.
          Mehr dazu in unserer{" "}
          <Link to="/datenschutz" className="underline text-primary hover:text-primary/80 transition-colors">
            Datenschutzerklärung
          </Link>.
        </p>

        {/* Category toggles */}
        {showDetails && (
          <div className="space-y-3 mb-4 border-t border-border pt-4">
            <ToggleRow
              label="Notwendig"
              description="Für die Grundfunktion der Website erforderlich."
              checked={true}
              disabled
            />
            <ToggleRow
              label="Analyse"
              description="Helfen uns zu verstehen, wie die Website genutzt wird."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
            <ToggleRow
              label="Marketing"
              description="Werden für personalisierte Werbung verwendet."
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          {!showDetails ? (
            <>
              <button
                onClick={acceptAll}
                className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Alle akzeptieren
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
              >
                Anpassen
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-secondary"
              >
                Ablehnen
              </button>
            </>
          ) : (
            <>
              <button
                onClick={saveSelection}
                className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Auswahl speichern
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
              >
                Alle akzeptieren
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (val: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
    <button
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative h-6 w-11 rounded-full transition-colors ${
        checked ? "bg-primary" : "bg-muted"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-primary-foreground shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  </div>
);

export default CookieConsent;
